// Chunk Error Handler Utility
// This utility provides comprehensive handling for webpack chunk loading errors

class ChunkErrorHandler {
  constructor() {
    this.retryAttempts = new Map();
    this.maxRetries = 3;
    this.retryDelay = 1000; // Start with 1 second
    this.isHandlingError = false;
  }

  // Enhanced chunk error detection
  isChunkError(error) {
    const chunkErrorIndicators = [
      "ChunkLoadError",
      "Loading chunk",
      "Loading CSS chunk",
      "chunk failed",
      "Failed to import",
      "Failed to fetch dynamically imported module",
      "TypeError: Failed to fetch",
      "NetworkError when attempting to fetch resource",
      "Script error",
      "Cannot read properties of undefined (reading 'call')",
      "Cannot read property 'call' of undefined",
    ];

    const errorMessage = error?.message || error?.toString() || "";
    const errorStack = error?.stack || "";
    const errorName = error?.name || "";

    return chunkErrorIndicators.some(
      (indicator) =>
        errorMessage.includes(indicator) ||
        errorStack.includes(indicator) ||
        errorName.includes(indicator)
    );
  }

  // Get chunk ID from error
  getChunkId(error) {
    const errorMessage = error?.message || error?.toString() || "";
    const errorStack = error?.stack || "";

    // Try to extract chunk ID from various error patterns
    const patterns = [
      /chunk[s]?\s+(\d+)/i,
      /(\d+-[a-f0-9]+\.js)/i,
      /\/([a-f0-9]+-[a-f0-9]+\.js)/i,
      /static\/chunks\/(\d+-[a-f0-9]+\.js)/i,
    ];

    for (const pattern of patterns) {
      const match = errorMessage.match(pattern) || errorStack.match(pattern);
      if (match) {
        return match[1];
      }
    }

    return "unknown";
  }

  // Handle chunk loading error with retry logic
  async handleChunkError(error, retryCallback = null) {
    if (this.isHandlingError) {
      console.log("🔄 Already handling a chunk error, skipping...");
      return false;
    }

    this.isHandlingError = true;

    try {
      const chunkId = this.getChunkId(error);
      const currentRetries = this.retryAttempts.get(chunkId) || 0;

      console.error("🚨 Chunk Load Error Detected:", {
        chunkId,
        currentRetries,
        maxRetries: this.maxRetries,
        error: error.message,
        stack: error.stack,
      });

      // Log error for monitoring
      this.logChunkError(error, chunkId, currentRetries);

      if (currentRetries >= this.maxRetries) {
        console.error(
          `❌ Max retries (${this.maxRetries}) exceeded for chunk ${chunkId}`
        );
        this.showUserNotification(error, chunkId);
        return false;
      }

      // Increment retry count
      this.retryAttempts.set(chunkId, currentRetries + 1);

      // Calculate exponential backoff delay
      const delay = this.retryDelay * Math.pow(2, currentRetries);

      console.log(
        `🔄 Retrying chunk ${chunkId} in ${delay}ms (attempt ${currentRetries + 1}/${this.maxRetries})`
      );

      // Wait before retry
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Clear module cache if possible
      this.clearModuleCache(chunkId);

      // Execute retry callback if provided
      if (retryCallback && typeof retryCallback === "function") {
        try {
          await retryCallback();
          console.log(`✅ Retry successful for chunk ${chunkId}`);
          this.retryAttempts.delete(chunkId); // Reset on success
          return true;
        } catch (retryError) {
          console.error(`❌ Retry failed for chunk ${chunkId}:`, retryError);
          // If it's still a chunk error, try again
          if (this.isChunkError(retryError)) {
            return this.handleChunkError(retryError, retryCallback);
          }
          throw retryError;
        }
      } else {
        // Default retry: reload the page
        console.log("🔄 No retry callback provided, reloading page...");
        window.location.reload();
        return true;
      }
    } catch (handlingError) {
      console.error("❌ Error in chunk error handler:", handlingError);
      return false;
    } finally {
      this.isHandlingError = false;
    }
  }

  // Clear module cache for specific chunk
  clearModuleCache(chunkId) {
    try {
      // Clear webpack module cache if available
      if (typeof window !== "undefined" && window.__webpack_require__) {
        const cache = window.__webpack_require__.cache;
        if (cache) {
          Object.keys(cache).forEach((key) => {
            if (key.includes(chunkId)) {
              delete cache[key];
              console.log(`🗑️ Cleared cache for module: ${key}`);
            }
          });
        }
      }
    } catch (error) {
      console.warn("⚠️ Failed to clear module cache:", error);
    }
  }

  // Log chunk error for monitoring with enhanced details
  logChunkError(error, chunkId, retryCount) {
    const errorInfo = {
      type: "chunk-load-error",
      chunkId,
      retryCount,
      maxRetries: this.maxRetries,
      message: error.message || "Unknown chunk error",
      errorName: error.name || "Unknown",
      stack: error.stack || "No stack trace",
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      connectionType: navigator.connection?.effectiveType || "unknown",
      downlink: navigator.connection?.downlink || "unknown",
      rtt: navigator.connection?.rtt || "unknown",
      onLine: navigator.onLine,
      cookieEnabled: navigator.cookieEnabled,
      language: navigator.language,
      platform: navigator.platform,
      memoryUsage: performance.memory
        ? {
            usedJSHeapSize: Math.round(
              performance.memory.usedJSHeapSize / 1024 / 1024
            ),
            totalJSHeapSize: Math.round(
              performance.memory.totalJSHeapSize / 1024 / 1024
            ),
            jsHeapSizeLimit: Math.round(
              performance.memory.jsHeapSizeLimit / 1024 / 1024
            ),
          }
        : "unavailable",
      loadTime: performance.now(),
      retryHistory: Array.from(this.retryAttempts.entries()),
    };

    // Determine severity based on retry count
    const severity =
      retryCount >= this.maxRetries
        ? "🔴 CRITICAL"
        : retryCount >= 2
          ? "🟡 WARNING"
          : "🔵 INFO";
    const isMaxRetries = retryCount >= this.maxRetries;

    // Slack notifications disabled - only signup notifications are enabled
    console.log("ℹ️ Skipping Slack notification for chunk error (notifications disabled except signups)");
    return;

    // Send enhanced notification to error logging endpoint
    fetch("/api/error-notification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text: `🧩 ${severity} Chunk Load Error ${isMaxRetries ? "(MAX RETRIES EXCEEDED)" : ""}`,
        blocks: [
          {
            type: "header",
            text: {
              type: "plain_text",
              text: `🧩 ${severity} Chunk Load Error Alert`,
            },
          },
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: isMaxRetries
                ? `*⚠️ CRITICAL: Max retries (${this.maxRetries}) exceeded for chunk loading*`
                : `*Chunk loading failed, retry attempt ${retryCount}/${this.maxRetries}*`,
            },
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: `*Chunk ID:* \`${chunkId}\`` },
              {
                type: "mrkdwn",
                text: `*Retry:* ${retryCount}/${this.maxRetries}`,
              },
              {
                type: "mrkdwn",
                text: `*Error Type:* ${error.name || "Unknown"}`,
              },
              {
                type: "mrkdwn",
                text: `*Online Status:* ${navigator.onLine ? "✅ Online" : "❌ Offline"}`,
              },
            ],
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Connection:* ${navigator.connection?.effectiveType || "Unknown"}`,
              },
              {
                type: "mrkdwn",
                text: `*Downlink:* ${navigator.connection?.downlink || "Unknown"} Mbps`,
              },
              {
                type: "mrkdwn",
                text: `*RTT:* ${navigator.connection?.rtt || "Unknown"} ms`,
              },
              { type: "mrkdwn", text: `*Platform:* ${navigator.platform}` },
            ],
          },
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*Error Message:*\n\`\`\`${error.message || "Unknown error"}\`\`\``,
            },
          },
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*Page URL:*\n${window.location.href}`,
            },
          },
          ...(errorInfo.memoryUsage !== "unavailable"
            ? [
                {
                  type: "section",
                  fields: [
                    {
                      type: "mrkdwn",
                      text: `*Memory Used:* ${errorInfo.memoryUsage.usedJSHeapSize} MB`,
                    },
                    {
                      type: "mrkdwn",
                      text: `*Memory Total:* ${errorInfo.memoryUsage.totalJSHeapSize} MB`,
                    },
                    {
                      type: "mrkdwn",
                      text: `*Memory Limit:* ${errorInfo.memoryUsage.jsHeapSizeLimit} MB`,
                    },
                    {
                      type: "mrkdwn",
                      text: `*Load Time:* ${Math.round(errorInfo.loadTime)} ms`,
                    },
                  ],
                },
              ]
            : []),
          ...(error.stack
            ? [
                {
                  type: "section",
                  text: {
                    type: "mrkdwn",
                    text: `*Stack Trace:*\n\`\`\`${error.stack.substring(0, 1000)}${error.stack.length > 1000 ? "\n...(truncated)" : ""}\`\`\``,
                  },
                },
              ]
            : []),
          ...(errorInfo.retryHistory.length > 0
            ? [
                {
                  type: "section",
                  text: {
                    type: "mrkdwn",
                    text: `*Active Retries:* ${errorInfo.retryHistory.map(([id, count]) => `${id}:${count}`).join(", ")}`,
                  },
                },
              ]
            : []),
          {
            type: "context",
            elements: [
              {
                type: "mrkdwn",
                text: `📱 ${navigator.userAgent.substring(0, 100)}${navigator.userAgent.length > 100 ? "..." : ""}`,
              },
            ],
          },
        ],
      }),
    }).catch((logError) => {
      console.warn(
        "⚠️ Failed to log chunk error to notification endpoint:",
        logError
      );

      // Fallback: send simplified notification
      fetch("/api/error-notification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: `🧩 Chunk Error (Fallback): ${error.message || "Unknown"} | Chunk: ${chunkId} | Retry: ${retryCount}/${this.maxRetries} | URL: ${window.location.href}`,
        }),
      }).catch((fallbackError) => {
        console.error(
          "❌ Failed to send fallback chunk error notification:",
          fallbackError
        );
      });
    });

    // Also log to console for immediate visibility with enhanced details
    console.group("🧩 CHUNK ERROR DETAILS");
    console.error("Severity:", severity);
    console.error("Error Info:", errorInfo);
    console.error("Original Error:", error);
    console.table({
      "Chunk ID": chunkId,
      "Retry Count": `${retryCount}/${this.maxRetries}`,
      Connection: navigator.connection?.effectiveType || "Unknown",
      Online: navigator.onLine,
      "Memory Used (MB)":
        errorInfo.memoryUsage !== "unavailable"
          ? errorInfo.memoryUsage.usedJSHeapSize
          : "N/A",
    });
    console.groupEnd();
  }

  // Show user-friendly notification
  showUserNotification(error, chunkId) {
    // Import toast dynamically to avoid circular dependencies
    import("@/hooks/use-toast")
      .then(({ toast }) => {
        toast({
          title: "Loading Error",
          description: "Some content failed to load. Please refresh the page.",
          variant: "destructive",
          action: {
            altText: "Refresh",
            onClick: () => window.location.reload(),
          },
          duration: 10000,
        });
      })
      .catch(() => {
        // Fallback notification
        const shouldReload = confirm(
          "Some content failed to load. Would you like to refresh the page?"
        );
        if (shouldReload) {
          window.location.reload();
        }
      });
  }

  // Reset retry attempts (useful for testing or manual resets)
  resetRetries(chunkId = null) {
    if (chunkId) {
      this.retryAttempts.delete(chunkId);
    } else {
      this.retryAttempts.clear();
    }
  }

  // Get current retry status
  getRetryStatus(chunkId = null) {
    if (chunkId) {
      return this.retryAttempts.get(chunkId) || 0;
    }
    return Object.fromEntries(this.retryAttempts);
  }
}

// Create global instance
const chunkErrorHandler = new ChunkErrorHandler();

// Global error handler for chunk errors
export function setupGlobalChunkErrorHandler() {
  // Handle unhandled promise rejections (common for dynamic imports)
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;

    if (chunkErrorHandler.isChunkError(error)) {
      console.log("🧩 Caught chunk error in unhandled rejection:", error);
      event.preventDefault(); // Prevent default browser behavior

      chunkErrorHandler.handleChunkError(error).catch((handlingError) => {
        console.error("❌ Failed to handle chunk error:", handlingError);
      });
    }
  });

  // Handle regular JavaScript errors
  window.addEventListener("error", (event) => {
    const error = event.error || new Error(event.message);

    if (chunkErrorHandler.isChunkError(error)) {
      console.log("🧩 Caught chunk error in global error handler:", error);
      event.preventDefault();

      chunkErrorHandler.handleChunkError(error).catch((handlingError) => {
        console.error("❌ Failed to handle chunk error:", handlingError);
      });
    }
  });

  console.log("✅ Global chunk error handler setup complete");
}

// Enhanced dynamic import wrapper with error handling
export function createRetryableImport(importFn, componentName = "Component") {
  return () => {
    const executeImport = () => importFn();

    return executeImport().catch((error) => {
      if (chunkErrorHandler.isChunkError(error)) {
        console.log(`🧩 Chunk error in ${componentName} import:`, error);

        return chunkErrorHandler
          .handleChunkError(error, executeImport)
          .then((success) => {
            if (success) {
              return executeImport();
            } else {
              throw new Error(`Failed to load ${componentName} after retries`);
            }
          });
      } else {
        // Re-throw non-chunk errors
        throw error;
      }
    });
  };
}

// React lazy wrapper with chunk error handling
export function createRetryableLazy(importFn, componentName = "Component") {
  const { lazy } = require("react");
  return lazy(createRetryableImport(importFn, componentName));
}

export default chunkErrorHandler;
